import Component from "@base/js/component";
import GlobalService from "@services/global-service";
import debounce from "../../base/js/util/debounce";
import breakpoint from "../../base/js/util/breakpoint";

export default class PrimaryNav extends Component {
  // Defines the element selector which will initialize this component
  static selector = ".primary-nav";

  // Defines children that needs to be queried as part of this component
  static children = {
    burger: ".primary-nav__burger",
    search: ".primary-nav__search",
    nav: ".primary-nav__nav",
    sublinks: [".primary-nav__sublinks"],
    primaryLinks: [".primary-nav__primary-link"],
    primaryItems: [".primary-nav__primary-item"],
  };

  // Holds padding needed to account for scrollbar
  scrollbarWidth = 0;

  // Pauses lastScroll recording during document scroll
  // Helpful in nav
  pauseScrollRecording = false;

  // Last scroll before nav expands
  lastScroll = window.pageYOffset;

  // Defines event handlers to attach to children
  eventHandlers = {
    burger: {
      click: this.onBurgerClick,
    },
    nav: {
      transitionend: this.onNavTransitionEnd,
    },
    search: {
      click: this.onSearchClick,
    },
    sublinks: {
      transitionend: this.onSublinksTransitionEnd,
    },
    primaryLinks: {
      click: this.onPrimaryLinksClick,
    },
  };

  constructor() {
    super();
    this.lastBreakpoint = breakpoint();
  }

  // Will be called upon component instantiation
  afterInit() {
    this.mainParent = document.querySelector(".main");

    this.determinePadding();
    this.applyScrollBehavior();

    // attach sublinks dropdown listeners
    this.children.primaryItems.forEach((primaryItem) => {
      const toggler = primaryItem.querySelector(".submenu-toggle");

      if (toggler) {
        this.toggleTouchDeviceClassOnBody(
          toggler,
          "primary-nav__primary-item__toggler--touch-device"
        );
        const sublinks = primaryItem.querySelector(".primary-nav__sublinks");
        this.toggleTouchDeviceClassOnBody(
          sublinks,
          "primary-nav__sublinks--touch-device"
        );

        toggler.addEventListener("click", () =>
          this.onToggleSublink(toggler, sublinks)
        );
      }
    });

    window.addEventListener(
      "resize",
      debounce(() => {
        if (breakpoint() !== this.lastBreakpoint) {
          if (this.isExpanded) {
            this.toggleDisplayVisibility();
            this.toggleExpanded(false);
            this.children.nav.classList.remove(
              "primary-nav__nav--transitioning"
            );
          }

          this.lastBreakpoint = breakpoint();
        }
      }, 100)
    );
  }

  isTouchDevice() {
    if ("ontouchstart" in window) return true;
  }

  toggleTouchDeviceClassOnBody(el, touchClass) {
    if (this.isTouchDevice()) {
      el.classList.add(touchClass);
    }
  }

  get isExpanded() {
    return this.element.classList.contains("primary-nav--expanded");
  }

  onBurgerClick(e) {
    e.preventDefault();

    this.toggleDisplayVisibility();

    const isBurgerOpen = this.isExpanded;

    // Should not autohide if burger is opening
    if (!isBurgerOpen) {
      this.removeReverseScrollTimeout();
    }

    // timeout to apply display properties
    setTimeout(() => {
      this.toggleExpanded(!isBurgerOpen);
    }, 50);

    // Close search overlay
    GlobalService.toggleSearchOverlay(false);
  }

  toggleDisplayVisibility() {
    const isNavigationExpanded = this.isExpanded;

    if (!isNavigationExpanded) {
      const displayProps = window.matchMedia("(max-width: 767px)").matches
        ? "block"
        : "flex";
      this.children.nav.style.display = displayProps;
    } else {
      this.children.nav.style.display = null;
    }
  }

  toggleExpanded(isExpanded = null) {
    const willExpand = isExpanded === "boolean" ? isExpanded : !this.isExpanded;

    this.children.nav.classList.add("primary-nav__nav--transitioning");
    this.element.classList.toggle("primary-nav--expanded", willExpand);
    document.body.classList.toggle("is-burger-nav-expanded", willExpand);

    if (willExpand) {
      this.pauseScrollRecording = true;
      this.toggleUserScroll(false);

      const { scrollY } = window;
      document.body.setAttribute("data-scroll", scrollY);

      document.body.style.top = `-${scrollY}px`;
      document.body.style.position = "fixed";
      document.body.style.width = "100vw";
    } else {
      this.toggleUserScroll(true);
      this.pauseScrollRecording = false;

      const scrollY = parseFloat(document.body.getAttribute("data-scroll"));

      document.body.style.removeProperty("position");
      document.body.style.removeProperty("width");
      document.body.style.removeProperty("top");

      window.scrollTo(0, scrollY);
    }
  }

  applyScrollBehavior() {
    document.addEventListener("scroll", this.onDocumentScroll.bind(this));

    // Re-hide reverse scroll on idle
    ["mouseenter", "mousemove", "touchstart", "touchmove"].forEach(
      (eventName) => {
        this.element.addEventListener(
          eventName,
          this.restartReverseScrollTimeout.bind(this),
          { passive: true }
        );
      }
    );
  }

  onDocumentScroll() {
    if (this.pauseScrollRecording) {
      return;
    }

    const currentScroll = window.pageYOffset;

    this.toggleReverseScrollState(
      currentScroll < this.lastScroll && currentScroll > 0
    );

    this.lastScroll = currentScroll;
  }

  toggleReverseScrollState(isReversed) {
    if (!this.isExpanded) {
      this.element.classList.toggle("primary-nav--reverse-scroll", isReversed);

      // Add timer to reset state after reverse scroll
      if (isReversed === true) {
        this.restartReverseScrollTimeout();
      } else {
        this.element.classList.toggle(
          "primary-nav--reverse-scroll",
          isReversed
        );
      }
    }
  }

  restartReverseScrollTimeout() {
    // Restart only if burger is not expanded
    if (!this.isExpanded) {
      this.removeReverseScrollTimeout();
      this.reverScrollTimeout = setTimeout(
        () => this.toggleReverseScrollState(false),
        3000
      );
    }
  }

  removeReverseScrollTimeout() {
    clearTimeout(this.reverScrollTimeout);
  }

  onNavTransitionEnd(e) {
    if (e.target === e.currentTarget) {
      this.children.nav.classList.remove("primary-nav__nav--transitioning");

      // Restart reverse scroll timer
      this.restartReverseScrollTimeout();
    }
  }

  onSearchClick(e) {
    e.preventDefault();
    GlobalService.toggleSearchOverlay();

    if (this.isExpanded) {
      this.toggleDisplayVisibility();

      // timeout to apply display properties
      setTimeout(() => {
        this.toggleExpanded(false);
      }, 50);
    }
  }

  determinePadding() {
    const divOne = document.createElement("div");
    const divTwo = document.createElement("div");

    divOne.style.width = "100px";
    divOne.style.overflow = "auto";
    divOne.style.position = "absolute";

    divTwo.style.width = "100%";
    divOne.appendChild(divTwo);

    document.body.appendChild(divOne);
    this.scrollbarWidth = divOne.offsetWidth - divTwo.offsetWidth;
    document.body.removeChild(divOne);
  }

  toggleUserScroll(allowScroll = null) {
    const shouldAllowScroll =
      typeof allowScroll === "boolean" ? allowScroll : !this.isExpanded;

    if (shouldAllowScroll) {
      GlobalService.allowScroll();
    } else {
      GlobalService.preventScroll();
    }
  }

  onPrimaryLinksClick(e) {
    const sublinks = e.currentTarget.nextElementSibling;
    const expandable =
      sublinks &&
      /expandable/i.test(
        window.getComputedStyle(sublinks, "before").getPropertyValue("content")
      );

    if (expandable) {
      e.preventDefault();

      const isExpanded = sublinks.classList.contains(
        "primary-nav__sublinks--expanded"
      );

      // Compute initial and target heights
      const initialHeight = `${isExpanded ? sublinks.scrollHeight : 0}px`;
      const targetHeight = `${isExpanded ? 0 : sublinks.scrollHeight}px`;

      // Initial animation height
      sublinks.style.height = initialHeight;

      // Start animation
      setTimeout(() => {
        sublinks.style.height = targetHeight;
      }, 100);
    }
  }

  onSublinksTransitionEnd(e) {
    if (e.currentTarget === e.target) {
      // Add classname
      e.currentTarget.classList.toggle("primary-nav__sublinks--expanded");

      // Clear height value used in animation
      e.currentTarget.style.height = "";
    }
  }

  onToggleSublink(el, sublinks) {
    const isActive = el.classList.contains(
      "primary-nav__primary-item__toggler--active"
    );

    if (isActive) {
      el.classList.remove("primary-nav__primary-item__toggler--active");
      sublinks.classList.remove("primary-nav__sublinks--active");
    } else {
      const activeToggler = document.querySelector(
        ".primary-nav .primary-nav__primary-item__toggler--active"
      );
      if (activeToggler) {
        activeToggler.click();
      }
      el.classList.add("primary-nav__primary-item__toggler--active");
      sublinks.classList.add("primary-nav__sublinks--active");
    }
  }
}

// Remove this line if this component is not intended
// to initialize on DOM load.
PrimaryNav.init();
